import React from 'react';

/**
 * Componente: LoadingSpinner
 * Spinner de carregamento reutilizável
 */
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  return (
    <div className={`animate-spin rounded-full border-b-2 border-indigo-600 ${sizes[size]} ${className}`}></div>
  );
};

/**
 * Componente: LoadingPage
 * Loading de página inteira
 */
export const LoadingPage = ({ message = 'Carregando...' }) => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <LoadingSpinner size="xl" className="mx-auto" />
        <p className="mt-4 text-gray-600">{message}</p>
      </div>
    </div>
  );
};

/**
 * Componente: LoadingOverlay
 * Overlay de carregamento sobre conteúdo
 */
export const LoadingOverlay = ({ message = 'Carregando...' }) => {
  return (
    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50 rounded-lg">
      <div className="text-center">
        <LoadingSpinner size="lg" className="mx-auto" />
        <p className="mt-3 text-gray-600 font-medium">{message}</p>
      </div>
    </div>
  );
};

/**
 * Componente: LoadingCard
 * Card skeleton para listas
 */
export const LoadingCard = () => {
  return (
    <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-20 h-20 bg-gray-200 rounded-lg"></div>
        <div className="flex-1">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente: LoadingList
 * Lista de cards skeleton
 */
export const LoadingList = ({ count = 3 }) => {
  return (
    <div className="space-y-6">
      {Array.from({ length: count }).map((_, index) => (
        <LoadingCard key={index} />
      ))}
    </div>
  );
};

/**
 * Componente: LoadingButton
 * Botão com estado de loading
 */
export const LoadingButton = ({ 
  isLoading, 
  children, 
  loadingText = 'Carregando...', 
  disabled,
  className = '',
  ...props 
}) => {
  return (
    <button
      disabled={isLoading || disabled}
      className={`${className} ${isLoading || disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center">
          <LoadingSpinner size="sm" className="mr-2" />
          {loadingText}
        </span>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Componente: LoadingTable
 * Skeleton para tabela
 */
export const LoadingTable = ({ rows = 5, columns = 4 }) => {
  return (
    <div className="animate-pulse">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {Array.from({ length: columns }).map((_, index) => (
                <th key={index} className="px-6 py-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: rows }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: columns }).map((_, colIndex) => (
                  <td key={colIndex} className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded"></div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Componente: LoadingBarbershopCard
 * Skeleton específico para card de barbearia
 */
export const LoadingBarbershopCard = () => {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-5">
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="flex items-center gap-4 mb-4">
          <div className="h-4 bg-gray-200 rounded w-20"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
};

/**
 * Componente: LoadingGrid
 * Grid de cards skeleton
 */
export const LoadingGrid = ({ count = 6, columns = 3 }) => {
  const gridCols = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <div className={`grid ${gridCols[columns]} gap-6`}>
      {Array.from({ length: count }).map((_, index) => (
        <LoadingBarbershopCard key={index} />
      ))}
    </div>
  );
};

const LoadingComponents = {
  LoadingSpinner,
  LoadingPage,
  LoadingOverlay,
  LoadingCard,
  LoadingList,
  LoadingButton,
  LoadingTable,
  LoadingBarbershopCard,
  LoadingGrid
};

export default LoadingComponents;
